/**
 * Serve-line rules shared by the cafeteria serve screen (a browser bundle)
 * and the Ordo server's prep counts (PROD-1484), so a grade / homeroom facet
 * or a meal-option chip means the same students on both sides: which filter
 * axis a campus's serve bucket lands on, a student's value on each axis, and
 * which chips the serve row shows (the same chips flag a student on the
 * printed prep sheet).
 */

/**
 * Meal-option chips hidden from the serve row (PROD-1428 item 9): menu-option
 * tags that say nothing a worker plating from the meal line does not already
 * read there. The dietary and custom options (Veg, GF/DF, DF/EF, Cereal, a
 * school's own "Dessert") stay.
 */
export const HIDDEN_SERVE_BUCKET_CODES: ReadonlySet<string> = new Set(['MAIN', 'BEST_SELLER', 'COLD_SANDO_SALAD'])

/** Whether a meal-option chip (a template bucket, by its code) shows on the serve row. */
export function isVisibleServeBucket(bucket: { code: string | null }): boolean {
  return !(bucket.code && HIDDEN_SERVE_BUCKET_CODES.has(bucket.code))
}

/** Serve-group dimensions whose bucket label belongs on the GRADE axis. */
const GRADE_DIMENSIONS: ReadonlySet<string> = new Set(['AGE_GROUP', 'GRADE'])

/**
 * Which filter axis a campus's serve bucket (`groupLabel`) lands on, by the
 * campus's `serveGroupBy`. HOMEROOM and the other non-grade groups (TEACHER,
 * ROOM, LUNCH_PERIOD) are the homeroom axis; `NONE` (one undivided list) and
 * an unset dimension carry no axis, so their labels are filed under neither.
 */
export function serveFilterAxis(dimension: string | null | undefined): 'grade' | 'homeroom' | null {
  if (dimension == null || dimension === 'NONE') {
    return null
  }
  return GRADE_DIMENSIONS.has(dimension) ? 'grade' : 'homeroom'
}

/** The student fields the axes read (a slice of the serve payload's per-student rows). */
export interface ServeStudentAxes {
  gradeRangeName: string | null
  rosterName: string | null
  /** The server-resolved serve bucket; its dimension is the campus `serveGroupBy`. */
  groupLabel?: string | null
}

/**
 * A student's grade-axis value. The rendered bucket wins when the campus
 * groups by grade (a GRADE campus shows "8" where `gradeRangeName` reads
 * "Grade 8", and the facet must offer the text on the card); otherwise the
 * grade display field carries the axis on its own.
 */
export function studentGradeAxis(student: ServeStudentAxes, serveGroupBy: string | null | undefined): string | null {
  return serveFilterAxis(serveGroupBy) === 'grade' && student.groupLabel ? student.groupLabel : student.gradeRangeName
}

/** A student's homeroom-axis value — the mirror of `studentGradeAxis`. */
export function studentHomeroomAxis(student: ServeStudentAxes, serveGroupBy: string | null | undefined): string | null {
  return serveFilterAxis(serveGroupBy) === 'homeroom' && student.groupLabel ? student.groupLabel : student.rosterName
}
